import { z } from 'zod';
import {
  pagingFields,
  parseSearchParams,
  searchCount,
  type SearchFieldKinds,
  searchId,
  searchText,
  toSearchParams,
} from './search-spec.ts';

/**
 * Property search filter spec (Smart Search, MET-3526).
 *
 * Same contract shape as the meter and customer specs: the translator emits
 * it, `GET /properties/filter` runs it, and it round-trips through the URL.
 * A property's meters are the sensors in its sensor groups (directly, or via a
 * master group's child), the same resolution the meter inspector uses.
 */

export const PROPERTY_SEARCH_SORT_COLUMNS = [
  'name',
  'customerName',
  'meterCount',
  'createdAt',
] as const;

export const propertySearchSchema = z
  .object({
    customerId: searchId.optional().describe('Only properties belonging to this customer'),
    text: searchText.optional().describe('Free-text match against property name or Metron ID'),
    meterCountMin: searchCount.optional().describe('At least this many meters'),
    meterCountMax: searchCount.optional().describe('At most this many meters'),
    ...pagingFields(PROPERTY_SEARCH_SORT_COLUMNS),
  })
  .strict()
  .refine(
    (s) =>
      s.meterCountMin === undefined || s.meterCountMax === undefined ||
      s.meterCountMin <= s.meterCountMax,
    { message: 'meterCountMin must not exceed meterCountMax', path: ['meterCountMin'] },
  );

export type PropertySearch = z.infer<typeof propertySearchSchema>;

/** One row of `GET /properties/filter`. */
export interface PropertySearchRow {
  id: number;
  name: string;
  metronId: string;
  customerId: number;
  customerName: string | null;
  meterCount: number;
  createdAt: string;
}

export interface PropertySearchResponse {
  properties: PropertySearchRow[];
  total: number;
  page: number;
  pageSize: number;
}

const PROPERTY_SEARCH_FIELDS: SearchFieldKinds = {
  customerId: 'number',
  text: 'string',
  meterCountMin: 'number',
  meterCountMax: 'number',
  page: 'number',
  pageSize: 'number',
  sortBy: 'string',
  sortDir: 'string',
};

/** Serialize a property search to URL params (defaults omitted; see `toSearchParams`). */
export function toPropertySearchParams(search: PropertySearch): URLSearchParams {
  return toSearchParams(search, PROPERTY_SEARCH_FIELDS);
}

/** Parse URL params into a validated property search (see `parseSearchParams`). */
export function parsePropertySearchParams(
  params: URLSearchParams,
): ReturnType<typeof propertySearchSchema.safeParse> {
  return parseSearchParams(propertySearchSchema, PROPERTY_SEARCH_FIELDS, params);
}
